<!-- MyComponent.vue -->
<script setup lang="ts">
import type { DayOfWeek } from 'v-calendar/dist/types/src/utils/date/helpers';

// @ts-expect-error error on import path
import 'v-calendar/style.css';
import { storeToRefs } from 'pinia';
import { DatePicker } from 'v-calendar';
import { computed, ref } from 'vue';

import useRouting from '@/composables/useRouting.js';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { Language } from '@/types/locale';

const props = withDefaults(defineProps<{
  rows?: number;
  columns?: number;
}>(), {
  rows: 1,
  columns: 2,
});

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const globalConfigurationStore = useGlobalConfigurationStore();
const { currentLocale, globalConfiguration } = storeToRefs(globalConfigurationStore);

const { defaultRoute } = useRouting();

const savedDates = computed(() => filters.value.filters.find(it => it.filterProperties.type === 'date_range')?.selection);
const existingRange = computed(() => savedDates.value
  ? {
      start: savedDates.value.startDate,
      end: savedDates.value.endDate,
    }
  : null);
const datesRange = ref(existingRange);

const anchorOnGridComponent = document.querySelector('yb-list');

// With an en-US locale, the week start on sunday. we change it to en-GB just for the calendar configuration
const firstCalendarDay = computed(() => currentLocale.value === Language.ENGLISH ? 2 : 2);

function handleDatesSelection(dates: { start: number; end: number }) {
  filtersStore.addDatesFilter(dates);
  if (globalConfiguration.value?.homeUrl && window.location.href === globalConfiguration.value.homeUrl) {
    if (defaultRoute.value)
      window.location.href = defaultRoute.value.url;
  }
  if (anchorOnGridComponent && window.innerWidth >= 1024)
    anchorOnGridComponent.scrollIntoView({ behavior: 'smooth' });
}
</script>

<template>
  <div>
    <DatePicker
      v-model.range.number="datesRange"
      range mode="date"
      :columns="props.columns"
      :rows="props.rows"
      :min-date="new Date()"
      :first-day-of-week="firstCalendarDay as DayOfWeek"
      :locale="currentLocale"
      expanded
      @update:model-value="(v: any) => handleDatesSelection(v)"
    />
  </div>
</template>

<style lang="css">
.vc-light.vc-attr,
.vc-light .vc-attr {
  --vc-highlight-outline-bg: var(--yb-calendar-highlight) !important;
  --vc-highlight-outline-border: var(--yb-calendar-primary) !important;
  --vc-highlight-outline-content-color: var(--yb-calandar-text-highlight) !important;

  --vc-highlight-light-bg: var(--yb-calendar-highlight) !important;
  --vc-highlight-light-content-color: var(--yb-calandar-text-highlight) !important;
  --vc-highlight-solid-bg: var(--yb-calendar-primary) !important;
  --vc-highlight-solid-content-color: var(--yb-calendar-text-selected) !important;
}

.vc-expanded {
  padding-top: 0.5rem;
}

.vc-focus:focus-within {
  box-shadow: 0 0 0 2px var(--yb-calendar-highlight) !important;
}

.vc-disabled {
  opacity: 30% !important;
}

.vc-day-content:hover:not(.vc-disabled) {
  color: var(--yb-color-primary);
}

.vc-bordered {
  border: none !important;
  border-color: var(--yb-calendar-primary) !important;
}

.vc-title,
.vc-arrow {
  background-color: transparent !important;
}

.vc-title {
  font-size: var(--yb-text-base) !important;
  font-weight: var(--yb-text-bold) !important;
}
</style>
